import { useEffect, useState } from "react";
import type { ThreadMessageLike } from "@assistant-ui/react";
import { apiClient } from "~/utils/apiClient";
import { convertAguiToThreadMessages } from "./useAgUiPersistence";

/**
 * The shared AG-UI state shape returned by the /connect endpoint.
 *
 * Contains artifacts from all prior turns (keyed by message ID) and a
 * null context_buffer placeholder. This is the initial state that
 * StateDeltaEvent patches are applied against during a run.
 */
export interface AgUiState {
  artifacts: Record<string, string[]>;
  context_buffer: null | {
    used_tokens: number;
    free_tokens: number;
  };
  enable_pipeline_tools: boolean;
}

interface ConnectResponse {
  state: AgUiState;
  messages: unknown[];
}

interface UseAgUiStateReturn {
  initialState: AgUiState | null;
  /** Pre-converted history from /connect so useAgUiPersistence can skip a fetch. */
  initialMessages: ThreadMessageLike[] | null;
}

/**
 * Fetches the AG-UI connect payload on mount.
 *
 * Returns both the state (passed as `initialState` to the HttpAgent) and
 * the converted message history so the persistence layer can skip the
 * separate round-trip.
 *
 * Each ConversationRuntime instance is keyed on conversationId, so this
 * hook only ever fetches once per mount.
 */
export function useAgUiState(baseUrl: string, conversationId: string | null): UseAgUiStateReturn {
  const [initialState, setInitialState] = useState<AgUiState | null>(null);
  const [initialMessages, setInitialMessages] = useState<ThreadMessageLike[] | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setInitialState({ artifacts: {}, context_buffer: null, enable_pipeline_tools: false });
      setInitialMessages([]);
      return;
    }

    let cancelled = false;

    apiClient<ConnectResponse>(`${baseUrl}/conversations/${conversationId}/connect`)
      .then((resp) => {
        if (!cancelled) {
          setInitialState(resp.state);
          setInitialMessages(
            resp.messages.length > 0
              ? convertAguiToThreadMessages(
                  resp.messages as Parameters<typeof convertAguiToThreadMessages>[0]
                )
              : []
          );
        }
      })
      .catch((error) => {
        console.error("[AG-UI] connect failed, using empty state:", error);
        if (!cancelled) {
          setInitialState({ artifacts: {}, context_buffer: null, enable_pipeline_tools: false });
          setInitialMessages(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, conversationId]);

  return { initialState, initialMessages };
}
